import React from "react";

// Oval "application theme" badge (matches the Story Building reference):
// top arc = "future" statement (orange), bottom arc = "reason" statement (purple),
// center = the unique-stories text. Each glyph is placed + rotated around the ring
// so both top and bottom text stay upright and read left-to-right.
const ORANGE = "#e8612c";
const PURPLE = "#5b2d8e";

function ArcText({ text, cx, cy, radiusX, radiusY, centerAngle, color, dir }) {
  const chars = [...(text || "")];
  if (!chars.length) return null;
  const degPerChar = 3.6;
  const total = Math.min(chars.length * degPerChar, 250);
  const start = centerAngle - (dir * total) / 2;
  const step = chars.length > 1 ? (dir * total) / (chars.length - 1) : 0;
  return chars.map((ch, i) => {
    const angle = start + i * step;
    const rad = (angle * Math.PI) / 180;
    const x = cx + radiusX * Math.cos(rad);
    const y = cy + radiusY * Math.sin(rad);
    const rot = dir > 0 ? angle + 90 : angle - 90; // keep glyph upright
    return (
      <text
        key={i}
        x={x}
        y={y}
        fill={color}
        fontSize="15"
        fontWeight="700"
        textAnchor="middle"
        dominantBaseline="middle"
        transform={`rotate(${rot} ${x} ${y})`}
      >
        {ch}
      </text>
    );
  });
}

// `ratio` = width / height (>1 => oval/landscape). Default a gentle oval.
export default function StoryThemeCircle({ topText, bottomText, centerText, size = 380, ratio = 1.3 }) {
  const w = Math.round(size * ratio);
  const h = size;
  const cx = w / 2;
  const cy = h / 2;
  const rx = w / 2 - 40;
  const ry = h / 2 - 34;
  const txRx = rx + 12;
  const txRy = ry + 12;
  const innerX = rx - 18;
  const innerY = ry - 16;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" style={{ maxWidth: w }}>
      <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill="none" stroke={PURPLE} strokeWidth="3" />
      {/* top arc, left→right over the top */}
      <ArcText text={topText} cx={cx} cy={cy} radiusX={txRx} radiusY={txRy} centerAngle={270} color={ORANGE} dir={1} />
      {/* bottom arc, left→right under the bottom */}
      <ArcText text={bottomText} cx={cx} cy={cy} radiusX={txRx} radiusY={txRy} centerAngle={90} color={PURPLE} dir={-1} />
      {centerText ? (
        <foreignObject x={cx - innerX} y={cy - innerY} width={innerX * 2} height={innerY * 2}>
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              padding: "0 6%",
              color: "#374151",
              fontSize: 15,
              lineHeight: 1.45,
            }}
          >
            {centerText}
          </div>
        </foreignObject>
      ) : null}
    </svg>
  );
}
